import { isApolloError } from '@apollo/client';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { GraphQLErrorExtensions } from 'graphql';
import { isNumber, isString, partition } from 'lodash-es';
import { GetStandaloneMeasuringPointsDocument, GetProjectCatalogDocument } from '@/graphql/types';
import { confirm } from '../../../../confirm/Confirm';
import copyModal from '../../../measuringPoint/copyModal';
import { addNotificationHack, NOTIFICATION_VARIANT } from '../../../../notifications/Notifications';
import { getClient } from '../../../../../utils/apollo-client';
import warning from '../../../../../utils/logger';
import { createPaginatedRefetchOptions } from '../../../../../utils/paginatedMutation';
import type { SwarmType } from '../../../../../enums';
import type { OptionsMenuItem } from '../../components/OptionsMenu';
import type { PERMISSION } from '../../../sharing/utils';
import { ObjectType, type ObjectTypeKey } from '../../../../../utils/objectType';
import getDeleteMessage from '../../../../../utils/messages';
import { getExpandedMeasuringPointsQuery } from '../../hooks/useLoadExpandedMeasuringPoints';

type RequiredPermissions = PERMISSION | PERMISSION[];
type MenuSpec = OptionsMenuItem & {
    requiredPermissions: RequiredPermissions;
    showWhenDemoMp?: boolean;
};
type OverviewEntityType = ObjectTypeKey;

// The catalog page carries the table rows and their summaries (status counts, data warnings,
// recent exceedances), so refetching it keeps both fresh. Only active queries are refetched. The
// expanded measuring point rows are not listed: an action on a measuring point or a project
// refetches only its own project's rows, see `getOverviewRefetchQueriesForProject`.
const OVERVIEW_REFRESH_QUERY_DOCUMENTS = [
    GetProjectCatalogDocument,
    GetStandaloneMeasuringPointsDocument,
] as const;

type ProjectRowsRefetchQuery = ReturnType<typeof getExpandedMeasuringPointsQuery>;
type OverviewRefetchQuery =
    | (typeof OVERVIEW_REFRESH_QUERY_DOCUMENTS)[number]
    | ProjectRowsRefetchQuery;

function isProjectRowsQuery(query: OverviewRefetchQuery): query is ProjectRowsRefetchQuery {
    return 'query' in query;
}

export function getOverviewRefetchQueriesForProject(
    projectId: string,
    archived: boolean
): ReadonlyArray<OverviewRefetchQuery> {
    return [
        ...OVERVIEW_REFRESH_QUERY_DOCUMENTS,
        getExpandedMeasuringPointsQuery(projectId, archived),
    ];
}

/**
 * After an action on a measuring point: the overview lists, and its own project's rows. A
 * standalone measuring point has no project rows, only the lists.
 */
export function getOverviewRefetchQueriesForMeasuringPoint(
    projectId: string | null,
    archived: boolean
): ReadonlyArray<OverviewRefetchQuery> {
    return projectId != null
        ? getOverviewRefetchQueriesForProject(projectId, archived)
        : OVERVIEW_REFRESH_QUERY_DOCUMENTS;
}

function getEntityLabel(type: OverviewEntityType): string {
    return ObjectType[type].translation;
}

function getObjectNameOrUnknown(objectName: string | null | undefined): string {
    return objectName ?? gettext('UNKNOWN');
}

function getArchiveMessage(objectType: string, objectName: string): string {
    return interpolate(
        gettext('ARCHIVE_OBJECT'),
        { object_type: objectType, object_name: objectName },
        true
    );
}

function getDearchiveMessage(objectType: string, objectName: string): string {
    return interpolate(
        gettext('DEARCHIVE_OBJECT'),
        { object_type: objectType, object_name: objectName },
        true
    );
}

function getUndoSharingMessage(
    type: OverviewEntityType,
    objectName: string | null | undefined
): string {
    const resolvedObjectName = getObjectNameOrUnknown(objectName);

    if (type === ObjectType.PROJECT.type) {
        return interpolate(
            gettext('UNDO_SHARING_PROJECT'),
            { project_name: resolvedObjectName },
            true
        );
    }

    return interpolate(
        gettext('UNDO_SHARING_MEASURING_POINT'),
        { measuring_point_name: resolvedObjectName },
        true
    );
}

export function getRestartMessage(sensorName: string | null | undefined): string {
    return interpolate(
        gettext('RESTART_DEVICE'),
        { sensor_name: sensorName ?? gettext('UNKNOWN_SWARM') },
        true
    );
}

export function getDetachMessage(sensorName: string, measuringPointName: string): string {
    return interpolate(
        gettext('DETACH_DEVICE'),
        { sensor_name: sensorName, measuring_point_name: measuringPointName },
        true
    );
}

function getCopyLabel(destination: OverviewEntityType): string {
    return interpolate(
        gettext('COPY_TO_MP_OR_PROJECT'),
        { type: getEntityLabel(destination) },
        true
    );
}

function getArchivedSuccessMessage(objectName: string): string {
    return interpolate(
        gettext('OVERVIEW_OBJECT_ARCHIVED_SUCCESS'),
        { object_name: objectName },
        true
    );
}

function getDearchivedSuccessMessage(objectName: string): string {
    return interpolate(
        gettext('OVERVIEW_OBJECT_DEARCHIVED_SUCCESS'),
        { object_name: objectName },
        true
    );
}

function getDeletedSuccessMessage(objectName: string): string {
    return interpolate(
        gettext('OVERVIEW_OBJECT_DELETED_SUCCESS'),
        { object_name: objectName },
        true
    );
}

// The coded errors carry the values their message needs. A missing one is a contract violation,
// so it throws instead of producing a message with a gap.
function readExtension<T>(
    extensions: GraphQLErrorExtensions,
    key: string,
    isValid: (value: unknown) => value is T
): T {
    const value: unknown = extensions[key];
    if (!isValid(value)) {
        throw new Error(`Error extension "${key}" is missing or has the wrong type.`);
    }

    return value;
}

function getMutationErrorMessage(error: unknown): string {
    const extensions =
        error instanceof Error && isApolloError(error)
            ? error.graphQLErrors[0]?.extensions
            : undefined;

    switch (extensions?.code) {
        case 'ENTITY_UNKNOWN':
            return gettext('ENTITY_NOT_FOUND');
        case 'INCORRECT_PERMISSION':
            return gettext('INCORRECT_PERMISSION_ERROR');
        case 'MEASURING_POINT_INVALID_SETTINGS':
            return interpolate(
                gettext('OVERVIEW_ACTION_INVALID_SETTINGS_ERROR'),
                { name: readExtension(extensions, 'name', isString) },
                true
            );
        case 'PROJECT_STAR_LIMIT_REACHED':
            return interpolate(
                gettext('PROJECT_STAR_LIMIT_REACHED'),
                { limit: readExtension(extensions, 'limit', isNumber) },
                true
            );
        default:
            return gettext('GENERIC_SERVER_ERROR');
    }
}

function getMutationErrorMessageOrGeneric(error: unknown): string {
    try {
        return getMutationErrorMessage(error);
    } catch (contractError) {
        // Report the broken error, but still tell the user that the action failed.
        warning({ message: contractError });
        return gettext('GENERIC_SERVER_ERROR');
    }
}

function rootResultHasSuccess(value: unknown): value is { success?: boolean } {
    if (!value || typeof value !== 'object') {
        return false;
    }

    return 'success' in value;
}

function refetchQueriesManually(queries: ReadonlyArray<OverviewRefetchQuery>) {
    const client = getClient();
    const [projectRowsQueries, listDocuments] = partition(queries, isProjectRowsQuery);

    return Promise.allSettled([
        client.refetchQueries({ include: listDocuments }),
        ...projectRowsQueries.map((query) =>
            client.query({ ...query, fetchPolicy: 'network-only' })
        ),
    ]);
}

export function runMutationWithRefresh<TData extends object, TVariables extends object>({
    mutation,
    variables,
    successMessage,
    refetchQueries,
    refetchQueriesOnFailure,
}: {
    mutation: TypedDocumentNode<TData, TVariables>;
    variables: TVariables;
    successMessage?: string;
    refetchQueries?: ReadonlyArray<OverviewRefetchQuery>;
    // Apollo skips `refetchQueries` for a failed mutation.
    refetchQueriesOnFailure?: ReadonlyArray<OverviewRefetchQuery>;
}) {
    return (async () => {
        const queriesToRefetch = [...(refetchQueries ?? OVERVIEW_REFRESH_QUERY_DOCUMENTS)];
        const paginatedRefetchOptions = createPaginatedRefetchOptions(queriesToRefetch);

        try {
            const result = await getClient().mutate({
                mutation,
                variables,
                // Refetch only the relevant overview catalog queries.
                ...paginatedRefetchOptions,
            });

            const responseData = result?.data;
            const firstResponseKey =
                responseData && typeof responseData === 'object'
                    ? Object.keys(responseData)[0]
                    : undefined;
            const rootResult =
                responseData && firstResponseKey
                    ? Reflect.get(responseData, firstResponseKey)
                    : undefined;
            const mutationSucceeded =
                !rootResult || !rootResultHasSuccess(rootResult) || rootResult.success === true;
            const shouldShowSuccess = Boolean(successMessage && mutationSucceeded);

            if (shouldShowSuccess && successMessage) {
                addNotificationHack({
                    message: successMessage,
                    variant: NOTIFICATION_VARIANT.SUCCESS,
                    scrollToTop: true,
                });
            }
        } catch (error) {
            addNotificationHack({
                message: getMutationErrorMessageOrGeneric(error),
                variant: NOTIFICATION_VARIANT.ERROR,
                scrollToTop: true,
            });
            warning({ message: error });

            if (refetchQueriesOnFailure) {
                await refetchQueriesManually(refetchQueriesOnFailure);
            }
        }
    })();
}

export function confirmAndRunMutation<TData extends object, TVariables extends object>(
    markdownMessage: string,
    mutation: TypedDocumentNode<TData, TVariables>,
    variables: TVariables,
    successMessage?: string,
    refetchQueries?: ReadonlyArray<OverviewRefetchQuery>,
    refetchQueriesOnFailure?: ReadonlyArray<OverviewRefetchQuery>
) {
    confirm({
        markdownMessage,
        onConfirm() {
            void runMutationWithRefresh({
                mutation,
                variables,
                successMessage,
                refetchQueries,
                refetchQueriesOnFailure,
            });
        },
    });
}

function toNumberIdOrWarn(id: string, actionLabel: string): number | null {
    const parsedId = Number(id);
    if (Number.isNaN(parsedId)) {
        warning(`Cannot run ${actionLabel} because id "${id}" is not a number.`);
        return null;
    }

    return parsedId;
}

function canShowItem({
    entityPermission,
    requiredPermissions,
    showWhenDemoMp = false,
    isDemoMp = false,
}: {
    entityPermission: PERMISSION | null | undefined;
    requiredPermissions: RequiredPermissions;
    showWhenDemoMp?: boolean;
    isDemoMp?: boolean;
}): boolean {
    if (showWhenDemoMp && isDemoMp) {
        return true;
    }
    if (!entityPermission) {
        return false;
    }

    const allowed = Array.isArray(requiredPermissions)
        ? requiredPermissions
        : [requiredPermissions];

    return allowed.includes(entityPermission);
}

export function buildMenuItems(
    specs: MenuSpec[],
    permission: PERMISSION | null | undefined,
    { isDemoMp = false }: { isDemoMp?: boolean } = {}
): OptionsMenuItem[] {
    return specs
        .filter((spec) =>
            canShowItem({
                entityPermission: permission,
                requiredPermissions: spec.requiredPermissions,
                showWhenDemoMp: spec.showWhenDemoMp,
                isDemoMp,
            })
        )
        .map(({ requiredPermissions: _permissions, showWhenDemoMp: _demo, ...item }) =>
            item.disabled || (!item.onClick && !item.to && !item.href)
                ? { label: item.label, disabled: true }
                : item
        );
}

export function handleCopyAction({
    id,
    swarmType,
    source,
    destination,
    refetchQueries = OVERVIEW_REFRESH_QUERY_DOCUMENTS,
}: {
    id: string;
    swarmType: SwarmType;
    source: OverviewEntityType;
    destination: OverviewEntityType;
    refetchQueries?: ReadonlyArray<OverviewRefetchQuery>;
}) {
    const parsedId = toNumberIdOrWarn(id, 'start copy modal');
    if (parsedId == null) {
        return;
    }

    copyModal({
        id: parsedId,
        swarmType,
        source,
        destination,
        refetchQueries,
        showRefreshCountdown: false,
    });
}

export function copyLabel(destination: OverviewEntityType) {
    return getCopyLabel(destination);
}

export function buildArchiveMenuSpec<TData extends object>({
    type,
    id,
    objectName,
    archived,
    requiredPermissions,
    mutation,
    refetchQueries,
    refetchQueriesOnFailure,
}: {
    type: OverviewEntityType;
    id: string;
    objectName: string;
    archived: boolean | null | undefined;
    requiredPermissions: RequiredPermissions;
    mutation: TypedDocumentNode<TData, { id: string; archived: boolean }>;
    refetchQueries?: ReadonlyArray<OverviewRefetchQuery>;
    refetchQueriesOnFailure?: ReadonlyArray<OverviewRefetchQuery>;
}): MenuSpec {
    const isArchived = archived === true;
    const confirmMessage = isArchived
        ? getDearchiveMessage(getEntityLabel(type), objectName)
        : getArchiveMessage(getEntityLabel(type), objectName);
    const successMessage = isArchived
        ? getDearchivedSuccessMessage(objectName)
        : getArchivedSuccessMessage(objectName);

    return {
        label: isArchived ? gettext('DEARCHIVE') : gettext('ARCHIVE'),
        requiredPermissions,
        onClick: () =>
            confirmAndRunMutation(
                confirmMessage,
                mutation,
                { id, archived: !isArchived },
                successMessage,
                refetchQueries,
                refetchQueriesOnFailure
            ),
    };
}

export function buildDeleteMenuSpec<TData extends object>({
    type,
    id,
    objectName,
    requiredPermissions,
    mutation,
    refetchQueries,
    refetchQueriesOnFailure,
}: {
    type: OverviewEntityType;
    id: string;
    objectName: string;
    requiredPermissions: RequiredPermissions;
    mutation: TypedDocumentNode<TData, { id: string }>;
    refetchQueries?: ReadonlyArray<OverviewRefetchQuery>;
    refetchQueriesOnFailure?: ReadonlyArray<OverviewRefetchQuery>;
}): MenuSpec {
    const confirmMessage = getDeleteMessage(getEntityLabel(type), objectName);
    const successMessage = getDeletedSuccessMessage(objectName);

    return {
        label: gettext('DELETE'),
        requiredPermissions,
        onClick: () =>
            confirmAndRunMutation(
                confirmMessage,
                mutation,
                { id },
                successMessage,
                refetchQueries,
                refetchQueriesOnFailure
            ),
    };
}

export function buildUndoSharingMenuSpec<TData extends object, TVariables extends object>({
    type,
    id,
    objectName,
    requiredPermissions,
    mutation,
    actionLabel,
    variablesFromNumericId,
    refetchQueries,
}: {
    type: OverviewEntityType;
    id: string;
    objectName: string | null | undefined;
    requiredPermissions: RequiredPermissions;
    mutation: TypedDocumentNode<TData, TVariables>;
    actionLabel: string;
    variablesFromNumericId: (id: number) => TVariables;
    refetchQueries?: ReadonlyArray<OverviewRefetchQuery>;
}): MenuSpec {
    return {
        id: `undo-sharing-${type.toLowerCase()}-${id}`,
        label: gettext('UNDO_SHARING'),
        requiredPermissions,
        onClick: () => {
            const numericId = toNumberIdOrWarn(id, actionLabel);
            if (numericId == null) {
                return;
            }

            confirmAndRunMutation(
                getUndoSharingMessage(type, objectName),
                mutation,
                variablesFromNumericId(numericId),
                getDeletedSuccessMessage(getObjectNameOrUnknown(objectName)),
                refetchQueries
            );
        },
    };
}
